<template>
  <Teleport to="body" :disabled="asPage">
    <div
      v-if="isOpen || asPage"
      :class="asPage
        ? 'cliente-detail-page-root flex flex-col flex-1 min-h-0 h-full'
        : 'fixed inset-0 z-[9999] md:z-[10000] flex items-center justify-center bg-black bg-opacity-50'"
    >
      <div
        :class="asPage
          ? 'cliente-page-panel cliente-page-panel--fill cliente-modal-panel relative flex flex-col flex-1 min-h-0 bg-white dark:bg-gray-800 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-[4px] shadow-sm'
          : 'modal-panel cliente-modal-panel relative flex flex-col bg-white dark:bg-gray-800 shadow-2xl w-[96vw] h-[94vh] md:w-[94vw] md:h-[92vh] lg:w-[80vw] lg:h-[90vh] mx-2 md:mx-3 max-h-[94vh] md:max-h-[92vh] lg:max-h-[90vh] overflow-hidden'"
        @click.stop
      >
        <div
          v-if="!asPage"
          class="modal-header flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 pr-12 sm:pr-3 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-secondary)] shrink-0"
        >
          <div class="flex min-w-0 items-center space-x-3">
            <div class="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-[4px] flex items-center justify-center shrink-0">
              <svg class="w-5 h-5 text-sky-600 dark:text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div class="flex min-w-0 items-center space-x-3">
              <div class="w-3 h-3 rounded-[4px] shrink-0" :style="{ backgroundColor: form.cor || '#0ea5e9' }" />
              <div class="min-w-0">
                <h2 class="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                  {{ headerTitle }}
                </h2>
                <p class="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
                  {{ t('workspaceClienteModal.subtitle') }}
                </p>
              </div>
            </div>
          </div>
          <div class="flex w-full items-center justify-between sm:w-auto sm:justify-end sm:space-x-3">
            <div class="flex items-center space-x-2">
              <Transition name="status-fade" mode="out-in">
                <div
                  v-if="saving"
                  key="salvando"
                  class="flex items-center space-x-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-[4px]"
                >
                  <svg class="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  <span class="hidden sm:inline text-xs font-medium text-blue-600 dark:text-blue-400">{{ t('workspaceClienteModal.saving') }}</span>
                </div>
                <div
                  v-else-if="savedFlash"
                  key="salvo"
                  class="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-[4px]"
                >
                  <svg class="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span class="hidden sm:inline text-xs font-medium text-green-600 dark:text-green-400">{{ t('workspaceClienteModal.saved') }}</span>
                </div>
                <div
                  v-else
                  key="pronto"
                  class="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-[4px]"
                >
                  <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  <span class="hidden sm:inline text-xs font-medium text-gray-500 dark:text-gray-400">{{ t('kanban.taskDetail.saveStatus.ready') }}</span>
                </div>
              </Transition>
            </div>
            <button
              type="button"
              class="absolute top-3 right-2 z-20 sm:static p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-[4px] transition-colors"
              :aria-label="t('common.close')"
              @click="close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <ClienteModalTabBar :model-value="tabBarActiveId" :tabs="visibleTabs" @update:model-value="onTabSelect">
          <template v-if="isEditMode" #actions>
            <button
              type="button"
              class="cliente-modal-tab-bar__archive-btn"
              :title="t('workspaceClienteModal.archiveCliente')"
              :aria-label="t('workspaceClienteModal.archiveCliente')"
              :disabled="archivingCliente"
              @click="arquivarCliente"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
              </svg>
            </button>
          </template>
        </ClienteModalTabBar>

        <div
          class="cliente-modal-body"
          :class="{ 'cliente-modal-body--flush': !asPage && (activeTab === 'historico' || activeTab === 'whatsapp') }"
        >
          <div v-if="clienteLoading" class="cliente-modal-loading">
            <div class="cliente-modal-loading__spinner" />
            <p>{{ t('common.loading') }}</p>
          </div>
          <template v-else>
          <p v-if="!isEditMode && (asPage || activeTab === 'dados')" class="cliente-modal-hint">
            {{ t('workspaceClienteModal.createHint') }}
          </p>

          <!-- Dados (na página só Dados no corpo; demais abas no drawer) -->
          <div v-if="asPage || activeTab === 'dados'" class="cliente-dados-layout">
            <div class="cliente-dados-main space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ClienteFormField
                  v-model="form.nome"
                  :label="t('workspaceClienteModal.fields.nome')"
                  required
                  :error="errors.nome"
                  @blur="validateNome"
                >
                  <template #icon>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  </template>
                </ClienteFormField>
                <ClienteFormField
                  v-model="form.cpf"
                  :label="t('workspaceClienteModal.fields.cpfCnpj')"
                  inputmode="numeric"
                  autocomplete="off"
                  @update:model-value="onCpfCnpjInput"
                  @blur="saveCliente"
                >
                  <template #icon>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2v-5M14 4h6m0 0v6m0-6L10 14"/></svg>
                  </template>
                </ClienteFormField>
                <ClienteFormField
                  v-model="form.nomeEmpresa"
                  :label="t('workspaceClienteModal.fields.nomeEmpresa')"
                  @blur="saveCliente"
                >
                  <template #icon>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                  </template>
                </ClienteFormField>
                <ClienteFormField
                  v-model="form.email"
                  :label="t('workspaceClienteModal.fields.email')"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  @blur="saveCliente"
                >
                  <template #icon>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  </template>
                </ClienteFormField>
                <ClienteFormField
                  v-model="form.telefoneWhatsapp"
                  :label="t('workspaceClienteModal.fields.whatsapp')"
                  type="tel"
                  inputmode="tel"
                  @update:model-value="onPhoneInput"
                  @blur="saveCliente"
                >
                  <template #icon>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                  </template>
                </ClienteFormField>
              </div>
              <ClienteFormField
                v-model="form.descricao"
                :label="t('workspaceClienteModal.fields.descricao')"
                multiline
                :rows="3"
                @blur="saveCliente"
              >
                <template #icon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/></svg>
                </template>
              </ClienteFormField>

              <ClienteDetailSection
                :title="t('workspaceClienteModal.fields.enderecos')"
                :subtitle="t('workspaceClienteModal.endereco.listSubtitle')"
                icon-class="from-emerald-500 to-teal-600"
              >
                <template #icon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                </template>
                <template #actions>
                  <ClienteIconBtn variant="add" :label="t('workspaceClienteModal.addEndereco')" @click="openEnderecoModal()" />
                </template>
                <div v-if="!form.enderecos?.length" class="cliente-empty-state">
                  <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                  <p>{{ t('workspaceClienteModal.endereco.empty') }}</p>
                  <ClienteIconBtn variant="add" :label="t('workspaceClienteModal.addEndereco')" @click="openEnderecoModal()" />
                </div>
                <ClienteEnderecoListItem
                  v-for="(end, idx) in form.enderecos"
                  :key="idx"
                  :endereco="end"
                  @edit="openEnderecoModal(end, idx)"
                  @remove="removeEndereco(idx)"
                />
              </ClienteDetailSection>
            </div>

            <aside class="cliente-dados-side space-y-4">
              <ClienteDetailSection
                :title="t('workspaceClienteModal.fields.contatos')"
                :subtitle="t('workspaceClienteModal.contato.listSubtitle')"
                icon-class="from-sky-500 to-blue-600"
              >
                <template #icon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                </template>
                <template #actions>
                  <ClienteIconBtn variant="add" :label="t('workspaceClienteModal.addContato')" @click="openContatoModal()" />
                </template>
                <div v-if="!form.contatos?.length" class="cliente-empty-state">
                  <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  <p>{{ t('workspaceClienteModal.contato.empty') }}</p>
                  <ClienteIconBtn variant="add" :label="t('workspaceClienteModal.addContato')" @click="openContatoModal()" />
                </div>
                <ClienteContatoListItem
                  v-for="(c, idx) in form.contatos"
                  :key="idx"
                  :contato="c"
                  @edit="openContatoModal(c, idx)"
                  @remove="removeContato(idx)"
                />
              </ClienteDetailSection>

              <ClienteDetailSection
                :title="t('kanban.taskDetail.sections.tags.title')"
                :subtitle="t('kanban.taskDetail.sections.tags.subtitle')"
              >
                <template #icon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>
                </template>
                <WorkspaceTagsPicker
                  :workspace-tags="workspaceTags"
                  :model-value="tagIds"
                  @update:model-value="onTagsChange"
                />
              </ClienteDetailSection>

              <ClienteDetailSection
                :title="t('kanban.taskDetail.sections.assignedUsers.title')"
                :subtitle="t('kanban.taskDetail.sections.assignedUsers.subtitle')"
                icon-class="from-orange-500 to-red-600"
              >
                <template #icon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/></svg>
                </template>
                <ClienteAssignedUsers
                  :workspace-users="workspaceUsers"
                  :assigned-ids="assignedUserIds"
                  @assign="showUserSelector = true"
                  @remove="removeUser"
                />
              </ClienteDetailSection>
            </aside>
          </div>

          <template v-if="!asPage">
          <!-- Histórico -->
          <div v-if="activeTab === 'historico'" class="cliente-tab-pane cliente-tab-pane--fill">
            <TaskChatPanel
              v-if="form.id"
              :cliente-id="form.id"
              :plan-blocked="chatPlanBlocked"
              :plan-gate-title="t('workspaceClienteModal.chat.planLock')"
              :plan-gate-description="t('workspaceClienteModal.chat.planLockDesc')"
              :incoming-bubble-light-text="true"
              class="h-full"
            />
          </div>

          <!-- Agendamento -->
          <div v-else-if="activeTab === 'agendamento'" class="max-w-3xl space-y-4">
            <ClienteDetailSection
              :title="t('kanban.taskDetail.sections.schedule.title')"
              :subtitle="t('workspaceClienteModal.agendamento.listSubtitle')"
            >
              <template #icon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              </template>
              <template #actions>
                <button
                  type="button"
                  class="px-3 py-1.5 text-xs font-semibold rounded-[4px] bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-sm"
                  @click="showScheduleModal = true"
                >
                  {{ t('workspaceClienteModal.agendamento.new') }}
                </button>
              </template>
              <div v-if="agendamentosLoading" class="text-sm text-gray-500 py-4">{{ t('common.loading') }}</div>
              <div v-else-if="!agendamentosAtivos.length && !agendamentosHistorico.length" class="cliente-empty-state cliente-empty-state--agendamento">
                <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                <p>{{ t('workspaceClienteModal.agendamento.empty') }}</p>
                <button type="button" class="cliente-empty-state__cta" @click="showScheduleModal = true">{{ t('workspaceClienteModal.agendamento.new') }}</button>
              </div>
              <template v-else>
                <div v-if="agendamentosAtivos.length" class="space-y-3">
                  <h4 class="cliente-ag-section-title">{{ t('workspaceClienteModal.agendamento.sectionUpcoming') }}</h4>
                  <ClienteAgendamentoCard
                    v-for="ag in agendamentosAtivos"
                    :key="ag.id"
                    :agendamento="ag"
                    :is-next="ag.id === nextAgendamentoId"
                    :locale="dateLocale"
                    :show-actions="true"
                    :show-delete="canDeleteAgendamento"
                    @status-change="updateAgendamentoStatus"
                    @delete="requestDeleteAgendamento(ag)"
                  />
                </div>
                <div v-if="agendamentosHistorico.length" class="space-y-3" :class="{ 'mt-6': agendamentosAtivos.length }">
                  <h4 class="cliente-ag-section-title">{{ t('workspaceClienteModal.agendamento.sectionHistory') }}</h4>
                  <ClienteAgendamentoCard
                    v-for="ag in agendamentosHistorico"
                    :key="'hist-' + ag.id"
                    :agendamento="ag"
                    :locale="dateLocale"
                    :show-actions="false"
                    :show-delete="canDeleteAgendamento"
                    @delete="requestDeleteAgendamento(ag)"
                  />
                </div>
              </template>
            </ClienteDetailSection>
          </div>

          <!-- Financeiro -->
          <div v-else-if="activeTab === 'financeiro'" class="max-w-3xl space-y-4">
            <ClienteDetailSection
              :title="t('kanban.taskDetail.sections.finance.title')"
              :subtitle="t('kanban.taskDetail.sections.finance.subtitle')"
              icon-class="from-emerald-500 to-teal-600"
            >
              <template #icon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </template>
              <template #actions>
                <button type="button" class="px-2 py-1 text-xs font-semibold rounded-[4px] bg-emerald-500 text-white" @click="openRecebivelModal()">
                  {{ t('kanban.taskDetail.sections.finance.addButton') }}
                </button>
              </template>
              <div v-if="!recebiveisOrdenados.length" class="cliente-empty-state cliente-empty-state--financeiro">
                <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <p>{{ t('workspaceClienteModal.financeiro.empty') }}</p>
                <button type="button" class="cliente-empty-state__cta" @click="openRecebivelModal()">{{ t('workspaceClienteModal.financeiro.add') }}</button>
              </div>
              <ClienteRecebivelCard
                v-for="item in recebiveisOrdenados"
                :key="item.id"
                :item="item"
                :locale="locale"
                :status-class="recebivelStatusClass(item)"
                :bar-class="recebivelBarClass(item)"
                :badge-class="recebivelBadgeClass(item)"
                :status-label="recebivelStatusLabel(item)"
                :format-date-only="formatDateOnly"
                :show-delete="canDeleteRecebivel"
                @edit="openRecebivelModal"
                @toggle-pago="toggleRecebivelPago"
                @delete="requestDeleteRecebivel"
              />
            </ClienteDetailSection>
          </div>

          <!-- Anexos -->
          <div v-else-if="activeTab === 'anexos'">
            <AnexosSection v-if="form.id" :cliente-id="form.id" icon-only-upload @anexos-updated="() => {}" />
          </div>

          <!-- WhatsApp (modal) -->
          <div v-else-if="activeTab === 'whatsapp'" class="cliente-tab-pane cliente-tab-pane--fill">
            <WhatsAppChatPanel
              v-if="showWhatsAppTab"
              ref="waPanelRef"
              entity-type="cliente"
              :entity-id="form.id"
              :workspace-id="workspaceId"
              :contact-name="form.nome"
              :contact-phone="form.telefoneWhatsapp"
              :active="panelActive && activeTab === 'whatsapp'"
              layout="tab"
            />
          </div>
          </template>
          </template>
        </div>

        <footer v-if="showClienteModalFooter" class="cliente-modal-footer shrink-0">
          <button
            v-if="!asPage"
            type="button"
            class="cliente-modal-btn cliente-modal-btn--secondary"
            @click="close"
          >
            <svg class="cliente-modal-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            <span>{{ t('common.close') }}</span>
          </button>
          <button
            type="button"
            class="cliente-modal-btn cliente-modal-btn--primary"
            :disabled="saving"
            @click="saveClienteExplicit"
          >
            <svg v-if="saving" class="cliente-modal-btn__icon cliente-modal-btn__icon--spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            <svg v-else class="cliente-modal-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <span>{{ saving ? t('workspaceClienteModal.saving') : (isEditMode ? t('workspaceClienteModal.saveChanges') : t('workspaceClienteModal.saveCreate')) }}</span>
          </button>
        </footer>
      </div>

      <UserSelectorModal
        :is-open="showUserSelector"
        :selected-users="assignedUserIds"
        @close="showUserSelector = false"
        @confirm="onUsersSelected"
      />

      <ScheduleModal
        :is-open="showScheduleModal"
        :cliente="{ id: form.id, workspaceId }"
        :motivos-agendamento="motivosAgendamento"
        @close="showScheduleModal = false"
        @scheduled="onScheduled"
      />

      <ClienteConfirmModal
        :open="!!confirmDeleteTarget"
        :title="t('workspaceClienteModal.agendamento.deleteConfirmTitle')"
        :message="deleteConfirmMessage"
        :loading="deleting"
        @cancel="cancelDeleteAgendamento"
        @confirm="confirmDeleteAgendamento"
      />

      <ClienteConfirmModal
        :open="!!confirmDeleteRecebivelTarget"
        :title="t('workspaceClienteModal.financeiro.deleteConfirmTitle')"
        :message="deleteRecebivelConfirmMessage"
        :loading="deletingRecebivel"
        title-id="cliente-recebivel-confirm-title"
        @cancel="cancelDeleteRecebivel"
        @confirm="confirmDeleteRecebivel"
      />

      <ClienteEnderecoFormModal
        :open="enderecoModalOpen"
        :initial="enderecoEdit"
        @close="enderecoModalOpen = false"
        @save="onEnderecoSaved"
      />

      <ClienteContatoFormModal
        :open="contatoModalOpen"
        :initial="contatoEdit"
        @close="contatoModalOpen = false"
        @save="onContatoSaved"
      />

      <ClienteRecebivelFormModal
        :open="showRecebivelForm"
        :initial="recebivelEdit"
        :locale="dateLocale"
        @close="closeRecebivelModal"
        @save="onRecebivelSaved"
      />

      <ClienteSideDrawer
        v-if="asPage"
        :open="!!sideDrawerTab"
        :title="sideDrawerTitle"
        :subtitle="sideDrawerSubtitle"
        :wide="sideDrawerWide"
        :body-class="sideDrawerBodyClass"
        :show-header="sideDrawerShowHeader"
        :aria-label="sideDrawerSubtitle"
        @close="closeSideDrawer"
      >
        <template v-if="sideDrawerShowHeader && sideDrawerTab === 'agendamento'" #icon>
          <span v-html="TAB_ICONS.agendamento" />
        </template>
        <template v-else-if="sideDrawerShowHeader && sideDrawerTab === 'financeiro'" #icon>
          <span v-html="TAB_ICONS.financeiro" />
        </template>
        <template v-else-if="sideDrawerShowHeader && sideDrawerTab === 'anexos'" #icon>
          <span v-html="TAB_ICONS.anexos" />
        </template>

        <div v-if="sideDrawerTab === 'historico'" class="cliente-tab-pane cliente-tab-pane--fill cliente-side-drawer-pane">
          <TaskChatPanel
            v-if="form.id"
            :cliente-id="form.id"
            :plan-blocked="chatPlanBlocked"
            :plan-gate-title="t('workspaceClienteModal.chat.planLock')"
            :plan-gate-description="t('workspaceClienteModal.chat.planLockDesc')"
            :incoming-bubble-light-text="true"
            class="h-full"
          />
        </div>

        <WhatsAppChatPanel
          v-else-if="sideDrawerTab === 'whatsapp' && showWhatsAppTab"
          ref="waPanelRef"
          entity-type="cliente"
          :entity-id="form.id"
          :workspace-id="workspaceId"
          :contact-name="form.nome"
          :contact-phone="form.telefoneWhatsapp"
          :active="!!sideDrawerTab"
          layout="tab"
          column-class="wa-drawer-chat"
          class="cliente-side-drawer-pane cliente-side-drawer-pane--fill"
        />

        <div v-else-if="sideDrawerTab === 'agendamento'" class="cliente-side-drawer-pane cliente-side-drawer-pane--agendamento space-y-4 p-0">
          <ClienteDetailSection
            :title="t('kanban.taskDetail.sections.schedule.title')"
            :subtitle="t('workspaceClienteModal.agendamento.listSubtitle')"
          >
            <template #icon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            </template>
            <template #actions>
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-semibold rounded-[4px] bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-sm"
                @click="showScheduleModal = true"
              >
                {{ t('workspaceClienteModal.agendamento.new') }}
              </button>
            </template>
            <div v-if="agendamentosLoading" class="text-sm text-gray-500 py-4">{{ t('common.loading') }}</div>
            <div v-else-if="!agendamentosAtivos.length && !agendamentosHistorico.length" class="cliente-empty-state cliente-empty-state--agendamento">
              <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              <p>{{ t('workspaceClienteModal.agendamento.empty') }}</p>
              <button type="button" class="cliente-empty-state__cta" @click="showScheduleModal = true">{{ t('workspaceClienteModal.agendamento.new') }}</button>
            </div>
            <template v-else>
              <div v-if="agendamentosAtivos.length" class="space-y-3">
                <h4 class="cliente-ag-section-title">{{ t('workspaceClienteModal.agendamento.sectionUpcoming') }}</h4>
                <ClienteAgendamentoCard
                  v-for="ag in agendamentosAtivos"
                  :key="ag.id"
                  :agendamento="ag"
                  :is-next="ag.id === nextAgendamentoId"
                  :locale="dateLocale"
                  :show-actions="true"
                  :show-delete="canDeleteAgendamento"
                  @status-change="updateAgendamentoStatus"
                  @delete="requestDeleteAgendamento(ag)"
                />
              </div>
              <div v-if="agendamentosHistorico.length" class="space-y-3" :class="{ 'mt-6': agendamentosAtivos.length }">
                <h4 class="cliente-ag-section-title">{{ t('workspaceClienteModal.agendamento.sectionHistory') }}</h4>
                <ClienteAgendamentoCard
                  v-for="ag in agendamentosHistorico"
                  :key="'hist-' + ag.id"
                  :agendamento="ag"
                  :locale="dateLocale"
                  :show-actions="false"
                  :show-delete="canDeleteAgendamento"
                  @delete="requestDeleteAgendamento(ag)"
                />
              </div>
            </template>
          </ClienteDetailSection>
        </div>

        <div v-else-if="sideDrawerTab === 'financeiro'" class="cliente-side-drawer-pane cliente-side-drawer-pane--financeiro p-0">
          <div class="cliente-financeiro-drawer">
            <div class="cliente-financeiro-drawer__toolbar">
              <p class="cliente-financeiro-drawer__hint">{{ t('workspaceClienteModal.financeiro.drawerHint') }}</p>
              <button type="button" class="cliente-financeiro-drawer__add" @click="openRecebivelModal()">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                {{ t('workspaceClienteModal.financeiro.add') }}
              </button>
            </div>

            <div v-if="financeiroDrawerStats.total" class="cliente-financeiro-drawer__stats">
              <button
                type="button"
                class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--pending"
                :class="financeiroDrawerStatClass('pending')"
                :aria-pressed="isFinanceiroDrawerFilterActive('pending')"
                @click="toggleFinanceiroDrawerFilter('pending')"
              >
                <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="cliente-financeiro-drawer__stat-body">
                  <span class="cliente-financeiro-drawer__stat-label">{{ t('workspaceClienteModal.financeiro.statPending') }}</span>
                  <strong class="cliente-financeiro-drawer__stat-value">{{ formatMoneyAmount(financeiroDrawerStats.pendingTotal, locale) }}</strong>
                  <span v-if="financeiroDrawerStats.openCount" class="cliente-financeiro-drawer__stat-meta">
                    {{ t('workspaceClienteModal.financeiro.statOpenCount', { count: financeiroDrawerStats.openCount }) }}
                  </span>
                </div>
              </button>
              <button
                type="button"
                class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--paid"
                :class="financeiroDrawerStatClass('paid')"
                :aria-pressed="isFinanceiroDrawerFilterActive('paid')"
                @click="toggleFinanceiroDrawerFilter('paid')"
              >
                <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="cliente-financeiro-drawer__stat-body">
                  <span class="cliente-financeiro-drawer__stat-label">{{ t('workspaceClienteModal.financeiro.statPaid') }}</span>
                  <strong class="cliente-financeiro-drawer__stat-value">{{ formatMoneyAmount(financeiroDrawerStats.paidTotal, locale) }}</strong>
                </div>
              </button>
              <button
                v-if="financeiroDrawerStats.overdueCount"
                type="button"
                class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--alert"
                :class="financeiroDrawerStatClass('overdue')"
                :aria-pressed="isFinanceiroDrawerFilterActive('overdue')"
                @click="toggleFinanceiroDrawerFilter('overdue')"
              >
                <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="cliente-financeiro-drawer__stat-body">
                  <span class="cliente-financeiro-drawer__stat-label">{{ t('workspaceClienteModal.financeiro.statOverdue') }}</span>
                  <strong class="cliente-financeiro-drawer__stat-value">{{ financeiroDrawerStats.overdueCount }}</strong>
                </div>
              </button>
            </div>

            <div v-if="!recebiveisOrdenados.length" class="cliente-financeiro-drawer__empty cliente-empty-state">
              <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <p>{{ t('workspaceClienteModal.financeiro.empty') }}</p>
              <button type="button" class="cliente-empty-state__cta" @click="openRecebivelModal()">{{ t('workspaceClienteModal.financeiro.add') }}</button>
            </div>

            <div v-else class="cliente-financeiro-drawer__list-wrap">
              <div class="cliente-financeiro-drawer__list-head">
                <h3 class="cliente-financeiro-drawer__list-title">{{ t('workspaceClienteModal.financeiro.title') }}</h3>
                <span class="cliente-financeiro-drawer__list-count">{{ recebiveisDrawerFiltrados.length }}</span>
              </div>
              <div
                v-if="!recebiveisDrawerFiltrados.length"
                class="cliente-financeiro-drawer__filter-empty"
              >
                <p>{{ t('workspaceClienteModal.financeiro.filterEmpty') }}</p>
                <button type="button" class="cliente-financeiro-drawer__filter-reset" @click="resetFinanceiroDrawerFilters">
                  {{ t('workspaceClienteModal.financeiro.filterShowAll') }}
                </button>
              </div>
              <div v-else class="cliente-financeiro-drawer__list">
              <ClienteRecebivelCard
                v-for="item in recebiveisDrawerFiltrados"
                :key="item.id"
                variant="drawer"
                :item="item"
                :locale="locale"
                :status-class="recebivelStatusClass(item)"
                :bar-class="recebivelBarClass(item)"
                :badge-class="recebivelBadgeClass(item)"
                :status-label="recebivelStatusLabel(item)"
                :format-date-only="formatDateOnly"
                :show-delete="canDeleteRecebivel"
                @edit="openRecebivelModal"
                @toggle-pago="toggleRecebivelPago"
                @delete="requestDeleteRecebivel"
              />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="sideDrawerTab === 'anexos'" class="cliente-side-drawer-pane cliente-side-drawer-pane--anexos p-0">
          <AnexosSection v-if="form.id" :cliente-id="form.id" icon-only-upload @anexos-updated="() => {}" />
        </div>
      </ClienteSideDrawer>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast, POSITION } from 'vue-toastification'
import api from '@/services/api'
import Swal from 'sweetalert2'
import { formatMoneyAmount } from '@/utils/format'
import { maskCpfCnpj, maskPhoneBr } from '@/utils/inputMasks'
import { useRecebiveisDisplay } from '@/composables/useRecebiveisDisplay'
import { usePlanoFeatures } from '@/composables/usePlanoFeatures'
import { useAgendamentoStaffDelete } from '@/composables/useAgendamentoStaffDelete'
import { useRecebivelStaffDelete } from '@/composables/useRecebivelStaffDelete'
import TaskChatPanel from './TaskChatPanel.vue'
import AnexosSection from './AnexosSection.vue'
import UserSelectorModal from './UserSelectorModal.vue'
import ScheduleModal from './ScheduleModal.vue'
import ClienteFormField from './cliente/ClienteFormField.vue'
import ClienteDetailSection from './cliente/ClienteDetailSection.vue'
import WorkspaceTagsPicker from './cliente/WorkspaceTagsPicker.vue'
import ClienteAssignedUsers from './cliente/ClienteAssignedUsers.vue'
import ClienteEnderecoFormModal from './cliente/ClienteEnderecoFormModal.vue'
import ClienteContatoFormModal from './cliente/ClienteContatoFormModal.vue'
import ClienteModalTabBar from './cliente/ClienteModalTabBar.vue'
import ClienteEnderecoListItem from './cliente/ClienteEnderecoListItem.vue'
import ClienteContatoListItem from './cliente/ClienteContatoListItem.vue'
import ClienteAgendamentoCard from './cliente/ClienteAgendamentoCard.vue'
import ClienteConfirmModal from './cliente/ClienteConfirmModal.vue'
import ClienteRecebivelCard from './cliente/ClienteRecebivelCard.vue'
import ClienteRecebivelFormModal from './cliente/ClienteRecebivelFormModal.vue'
import ClienteIconBtn from './cliente/ClienteIconBtn.vue'
import ClienteSideDrawer from './cliente/ClienteSideDrawer.vue'
import WhatsAppChatPanel from './WhatsAppChatPanel.vue'
import { pushEscapeLayer } from '@/utils/escapeLayerStack'
import '@/assets/styles/cliente-modal-theme.css'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  asPage: { type: Boolean, default: false },
  cliente: { type: Object, default: null },
  /** ID estável (evita recarregar ao re-renderizar o pai com `{ id }` inline). */
  clienteId: { type: Number, default: null },
  workspaceId: { type: Number, required: true },
  workspaceTags: { type: Array, default: () => [] },
  workspaceUsers: { type: Array, default: () => [] },
  motivosAgendamento: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'updated', 'deleted', 'archived', 'title-change'])

const panelActive = computed(() => props.asPage || props.isOpen)

const resolvedClienteId = computed(() => {
  if (props.clienteId != null && !Number.isNaN(Number(props.clienteId))) {
    return Number(props.clienteId)
  }
  const fromProp = props.cliente?.id
  if (fromProp != null && !Number.isNaN(Number(fromProp))) {
    return Number(fromProp)
  }
  return null
})

const loadedClienteId = ref(null)

const { t, locale } = useI18n()
const toast = useToast()
const planoFeatures = usePlanoFeatures()
const { formatDateOnly, recebivelStatusKey, recebivelStatusClass, recebivelStatusLabel, recebivelBadgeClass, recebivelBarClass } = useRecebiveisDisplay()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const TAB_ICONS = {
  dados: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>',
  historico: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"/></svg>',
  agendamento: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
  financeiro: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
  anexos: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 4 0 108.486 8.486L20.5 13"/></svg>',
  whatsapp: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>'
}

const waPanelRef = ref(null)
const whatsAppConnected = ref(false)
const PAGE_DRAWER_TABS = ['historico', 'whatsapp', 'agendamento', 'financeiro', 'anexos']
const sideDrawerTab = ref(null)

const planLoaded = ref(false)
const activeTab = ref('dados')
const saving = ref(false)
const savedFlash = ref(false)
const errors = ref({ nome: '' })
const showUserSelector = ref(false)
const showScheduleModal = ref(false)
const agendamentos = ref([])
const agendamentosLoading = ref(false)
const clienteLoading = ref(false)
const archivingCliente = ref(false)

const enderecoModalOpen = ref(false)
const enderecoEdit = ref(null)
const enderecoEditIndex = ref(-1)
const contatoModalOpen = ref(false)
const contatoEdit = ref(null)
const contatoEditIndex = ref(-1)

const form = ref(emptyForm())
const recebiveis = ref([])
const showRecebivelForm = ref(false)
const recebivelEditingId = ref(null)
const recebivelEdit = ref(null)

const isEditMode = computed(() => !!form.value.id)

const headerTitle = computed(() =>
  isEditMode.value
    ? t('workspaceClienteModal.headerTitleEdit')
    : t('workspaceClienteModal.headerTitleCreate')
)

const showWhatsAppTab = computed(() => isEditMode.value && whatsAppConnected.value)

/** Rodapé: na página sempre visível (só Dados no corpo); no modal oculta em histórico/whatsapp inline. */
const showClienteModalFooter = computed(() => {
  if (props.asPage) return true
  return activeTab.value !== 'historico' && activeTab.value !== 'whatsapp'
})

const isPageDrawerTab = (tab) => props.asPage && PAGE_DRAWER_TABS.includes(tab)

const onTabSelect = async (tab) => {
  if (!props.asPage) {
    activeTab.value = tab
    if (tab === 'whatsapp' && showWhatsAppTab.value) {
      await refreshWhatsAppStatus()
      await nextTick()
      await waPanelRef.value?.refreshWhatsAppStatus?.()
      await waPanelRef.value?.loadChatHistory?.()
    }
    return
  }
  if (tab === 'dados') {
    closeSideDrawer()
    activeTab.value = 'dados'
    return
  }
  if (isPageDrawerTab(tab)) {
    if (tab === 'whatsapp' && !showWhatsAppTab.value) return
    sideDrawerTab.value = tab
    await onSideDrawerOpened(tab)
    return
  }
  closeSideDrawer()
  activeTab.value = tab
}

const closeSideDrawer = () => {
  sideDrawerTab.value = null
}

const onSideDrawerOpened = async (tab) => {
  if (tab === 'whatsapp') {
    await refreshWhatsAppStatus()
    await nextTick()
    await waPanelRef.value?.refreshWhatsAppStatus?.()
    await waPanelRef.value?.loadChatHistory?.()
  }
  if (tab === 'agendamento') {
    await loadAgendamentos()
  }
  if (tab === 'financeiro') {
    await loadStaffRole()
  }
}

const tabBarActiveId = computed(() => {
  if (props.asPage && sideDrawerTab.value) return sideDrawerTab.value
  return activeTab.value
})

const sideDrawerTitle = computed(() => {
  const tab = sideDrawerTab.value
  if (!tab) return ''
  const labels = {
    historico: t('workspaceClienteModal.tabs.historico'),
    whatsapp: t('workspaceClienteModal.tabs.whatsapp'),
    agendamento: t('workspaceClienteModal.tabs.agendamento'),
    financeiro: t('workspaceClienteModal.tabs.financeiro'),
    anexos: t('workspaceClienteModal.tabs.anexos')
  }
  return form.value.nome || labels[tab] || ''
})

const sideDrawerSubtitle = computed(() => {
  const tab = sideDrawerTab.value
  if (!tab) return ''
  const labels = {
    historico: t('workspaceClienteModal.tabs.historico'),
    whatsapp: t('workspaceClienteModal.tabs.whatsapp'),
    agendamento: t('workspaceClienteModal.tabs.agendamento'),
    financeiro: t('workspaceClienteModal.tabs.financeiro'),
    anexos: t('workspaceClienteModal.tabs.anexos')
  }
  return labels[tab] || ''
})

const sideDrawerWide = computed(() => ['historico', 'whatsapp'].includes(sideDrawerTab.value))

const sideDrawerShowHeader = computed(() => !['historico', 'whatsapp'].includes(sideDrawerTab.value))

const sideDrawerBodyClass = computed(() => {
  if (['historico', 'whatsapp', 'agendamento', 'financeiro'].includes(sideDrawerTab.value)) {
    return 'cliente-side-drawer-body--flush'
  }
  return ''
})

const allTabs = computed(() => {
  const tabs = [
    { id: 'dados', label: t('workspaceClienteModal.tabs.dados'), icon: TAB_ICONS.dados },
    { id: 'historico', label: t('workspaceClienteModal.tabs.historico'), icon: TAB_ICONS.historico },
    { id: 'agendamento', label: t('workspaceClienteModal.tabs.agendamento'), icon: TAB_ICONS.agendamento },
    { id: 'financeiro', label: t('workspaceClienteModal.tabs.financeiro'), icon: TAB_ICONS.financeiro },
    { id: 'anexos', label: t('workspaceClienteModal.tabs.anexos'), icon: TAB_ICONS.anexos }
  ]
  if (showWhatsAppTab.value) {
    tabs.splice(2, 0, {
      id: 'whatsapp',
      label: t('workspaceClienteModal.tabs.whatsapp'),
      icon: TAB_ICONS.whatsapp
    })
  }
  return tabs
})

const visibleTabs = computed(() => {
  if (!isEditMode.value) {
    return allTabs.value.filter(tab => tab.id === 'dados')
  }
  return allTabs.value
})

const refreshWhatsAppStatus = async () => {
  if (!props.workspaceId) {
    whatsAppConnected.value = false
    return
  }
  try {
    const r = await api.get(`/whatsapp/workspace/${props.workspaceId}`)
    whatsAppConnected.value = String(r.data?.status || '').toUpperCase() === 'CONNECTED'
  } catch {
    whatsAppConnected.value = false
  }
}

const chatPlanBlocked = computed(() => {
  if (!planLoaded.value || planoFeatures.loading.value) return false
  return !planoFeatures.can('servicosHistoricoChat')
})

const tagIds = computed(() => parseJsonArray(form.value.tags).map(Number))

const assignedUserIds = computed(() => parseJsonArray(form.value.usuariosAtribuidos).map(Number))

const recebiveisOrdenados = computed(() =>
  [...recebiveis.value].sort((a, b) => String(a.dataVencimento || '').localeCompare(String(b.dataVencimento || '')))
)

const FINANCEIRO_DRAWER_FILTER_KEYS = ['pending', 'paid', 'overdue']

const financeiroDrawerFilters = ref(new Set(FINANCEIRO_DRAWER_FILTER_KEYS))

function resetFinanceiroDrawerFilters () {
  financeiroDrawerFilters.value = new Set(FINANCEIRO_DRAWER_FILTER_KEYS)
}

watch(sideDrawerTab, (tab) => {
  if (tab === 'financeiro') resetFinanceiroDrawerFilters()
})

function isFinanceiroDrawerFilterActive (key) {
  return financeiroDrawerFilters.value.has(key)
}

function financeiroDrawerStatClass (key) {
  return isFinanceiroDrawerFilterActive(key)
    ? 'cliente-financeiro-drawer__stat--filter-active'
    : 'cliente-financeiro-drawer__stat--filter-inactive'
}

function toggleFinanceiroDrawerFilter (key) {
  const current = financeiroDrawerFilters.value

  if (current.size === FINANCEIRO_DRAWER_FILTER_KEYS.length) {
    financeiroDrawerFilters.value = new Set([key])
    return
  }

  if (current.size === 1 && current.has(key)) {
    resetFinanceiroDrawerFilters()
    return
  }

  const next = new Set(current)
  if (next.has(key)) {
    if (next.size <= 1) return
    next.delete(key)
  } else {
    next.add(key)
  }
  financeiroDrawerFilters.value = next
}

function recebivelMatchesDrawerFilter (item) {
  const filters = financeiroDrawerFilters.value
  if (filters.size === FINANCEIRO_DRAWER_FILTER_KEYS.length) return true

  const status = recebivelStatusKey(item)
  const isPaid = !!item.pago
  const isOverdue = !isPaid && status === 'overdue'
  const isPendingOpen = !isPaid && status !== 'overdue'

  if (filters.has('paid') && isPaid) return true
  if (filters.has('overdue') && isOverdue) return true
  if (filters.has('pending') && isPendingOpen) return true
  return false
}

const recebiveisDrawerFiltrados = computed(() =>
  recebiveisOrdenados.value.filter(recebivelMatchesDrawerFilter)
)

const financeiroDrawerStats = computed(() => {
  let pendingTotal = 0
  let paidTotal = 0
  let overdueCount = 0
  let openCount = 0
  for (const item of recebiveisOrdenados.value) {
    const valor = Number(item.valor) || 0
    if (item.pago) {
      paidTotal += valor
    } else {
      pendingTotal += valor
      openCount += 1
      if (recebivelStatusKey(item) === 'overdue') overdueCount += 1
    }
  }
  return { pendingTotal, paidTotal, overdueCount, openCount, total: recebiveisOrdenados.value.length }
})

function parseAgendamentoDateTime (ag) {
  const raw = ag?.dataAgendamento
  if (!raw) return null
  const hora = String(ag.horario || '00:00').slice(0, 5)
  const d = new Date(String(raw).includes('T') ? raw : `${raw}T${hora}:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function normalizeAgendamentoStatus (ag) {
  return String(ag?.status || 'AGENDADO').trim().toUpperCase()
}

function isAgendamentoFuture (ag) {
  const d = parseAgendamentoDateTime(ag)
  return d != null && d >= new Date()
}

function compareAgendamentoAsc (a, b) {
  const da = parseAgendamentoDateTime(a)?.getTime() ?? 0
  const db = parseAgendamentoDateTime(b)?.getTime() ?? 0
  return da - db
}

function compareAgendamentoDesc (a, b) {
  return compareAgendamentoAsc(b, a)
}

const agendamentosAtivos = computed(() =>
  agendamentos.value
    .filter((ag) => normalizeAgendamentoStatus(ag) === 'AGENDADO' && isAgendamentoFuture(ag))
    .sort(compareAgendamentoAsc)
)

const agendamentosHistorico = computed(() => {
  const ativoIds = new Set(agendamentosAtivos.value.map((a) => a.id))
  return agendamentos.value
    .filter((ag) => !ativoIds.has(ag.id))
    .sort(compareAgendamentoDesc)
})

const nextAgendamentoId = computed(() => agendamentosAtivos.value[0]?.id ?? null)

function emptyForm () {
  return {
    id: null,
    nome: '',
    cpf: '',
    nomeEmpresa: '',
    email: '',
    descricao: '',
    cor: '#0ea5e9',
    telefoneWhatsapp: '',
    tags: '[]',
    usuariosAtribuidos: '[]',
    enderecos: [],
    contatos: []
  }
}

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const parseRecebiveis = (value) => {
  if (Array.isArray(value)) return value
  if (!value) return []
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const validateNome = () => {
  errors.value.nome = form.value.nome?.trim() ? '' : t('workspaceClienteModal.validation.nomeRequired')
}

const onCpfCnpjInput = (v) => {
  form.value.cpf = maskCpfCnpj(v)
}

const onPhoneInput = (v) => {
  form.value.telefoneWhatsapp = maskPhoneBr(v)
}

const onTagsChange = async (ids) => {
  form.value.tags = JSON.stringify(ids)
  if (isEditMode.value) await saveCliente()
}

const removeUser = async (userId) => {
  const next = assignedUserIds.value.filter(id => Number(id) !== Number(userId))
  form.value.usuariosAtribuidos = JSON.stringify(next)
  await saveCliente()
}

const onUsersSelected = async (userIds) => {
  form.value.usuariosAtribuidos = JSON.stringify(Array.isArray(userIds) ? userIds : [])
  showUserSelector.value = false
  await saveCliente()
}

const openEnderecoModal = (item = null, idx = -1) => {
  enderecoEdit.value = item ? { ...item } : null
  enderecoEditIndex.value = idx
  enderecoModalOpen.value = true
}

const onEnderecoSaved = async (payload) => {
  if (!form.value.enderecos) form.value.enderecos = []
  if (enderecoEditIndex.value >= 0) {
    form.value.enderecos[enderecoEditIndex.value] = payload
  } else {
    form.value.enderecos.push(payload)
  }
  await saveCliente()
}

const removeEndereco = async (idx) => {
  form.value.enderecos.splice(idx, 1)
  await saveCliente()
}

const openContatoModal = (item = null, idx = -1) => {
  contatoEdit.value = item ? { ...item } : null
  contatoEditIndex.value = idx
  contatoModalOpen.value = true
}

const onContatoSaved = async (payload) => {
  if (!form.value.contatos) form.value.contatos = []
  if (contatoEditIndex.value >= 0) {
    form.value.contatos[contatoEditIndex.value] = payload
  } else {
    form.value.contatos.push(payload)
  }
  await saveCliente()
}

const removeContato = async (idx) => {
  form.value.contatos.splice(idx, 1)
  await saveCliente()
}

const jsonField = (value, fallback = '[]') => {
  if (value == null || value === '') return fallback
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return fallback
  }
}

const buildPayload = () => ({
  workspaceId: Number(form.value.workspaceId ?? props.workspaceId),
  nome: form.value.nome?.trim(),
  cpf: form.value.cpf || null,
  nomeEmpresa: form.value.nomeEmpresa?.trim() || null,
  email: form.value.email?.trim() || null,
  descricao: form.value.descricao || null,
  cor: form.value.cor || null,
  telefoneWhatsapp: form.value.telefoneWhatsapp || null,
  tags: jsonField(form.value.tags),
  usuariosAtribuidos: jsonField(form.value.usuariosAtribuidos),
  enderecos: (form.value.enderecos || []).map((e) => ({
    tipo: e.tipo || 'RESIDENCIAL',
    cep: e.cep || null,
    logradouro: e.logradouro || null,
    numero: e.numero || null,
    complemento: e.complemento || null,
    bairro: e.bairro || null,
    cidade: e.cidade || null,
    estado: e.estado || null,
    principal: !!e.principal
  })),
  contatos: (form.value.contatos || []).map((c) => ({
    tipo: c.tipo,
    valor: c.valor,
    principal: !!c.principal
  }))
})

const saveCliente = async () => {
  validateNome()
  if (!form.value.nome?.trim()) return false
  saving.value = true
  try {
    let response
    if (form.value.id) {
      response = await api.put(`/clientes/${form.value.id}`, buildPayload())
    } else {
      response = await api.post('/clientes', buildPayload())
      applyCliente(response.data)
      loadedClienteId.value = response.data?.id ? Number(response.data.id) : loadedClienteId.value
    }
    savedFlash.value = true
    setTimeout(() => { savedFlash.value = false }, 2000)
    emit('updated', response.data)
    return true
  } catch (error) {
    console.error('Erro ao salvar cliente:', error)
    let serverMsg = error?.response?.data?.error
    if (!serverMsg && typeof error?.response?.data === 'string') {
      try {
        serverMsg = JSON.parse(error.response.data)?.error
      } catch { /* ignore */ }
    }
    toast.error(serverMsg || t('workspaceClienteModal.validation.saveError'), { position: POSITION.BOTTOM_RIGHT })
    return false
  } finally {
    saving.value = false
  }
}

const saveClienteExplicit = async () => {
  validateNome()
  if (!form.value.nome?.trim()) {
    toast.error(t('workspaceClienteModal.validation.nomeRequired'), { position: POSITION.BOTTOM_RIGHT })
    return
  }
  const wasCreate = !form.value.id
  const ok = await saveCliente()
  if (!ok) return

  if (wasCreate) {
    toast.success(t('workspaceClienteModal.saved'), { position: POSITION.BOTTOM_RIGHT })
    if (!props.asPage) {
      close()
    }
  }
}

const saveRecebiveis = async () => {
  if (!form.value.id) return
  try {
    const json = JSON.stringify(recebiveis.value)
    const response = await api.put(`/clientes/${form.value.id}/recebiveis`, { recebiveisJson: json })
    recebiveis.value = parseRecebiveis(response.data?.recebiveisJson)
    emit('updated', response.data)
  } catch (error) {
    console.error('Erro ao salvar recebíveis:', error)
  }
}

const applyCliente = (data) => {
  if (!data) return
  form.value = {
    ...emptyForm(),
    ...data,
    enderecos: data.enderecos || [],
    contatos: data.contatos || []
  }
  recebiveis.value = parseRecebiveis(data.recebiveisJson)
  emitTitleChange()
}

const loadAgendamentos = async () => {
  if (!form.value.id) return
  await loadStaffRole()
  agendamentosLoading.value = true
  try {
    const { data } = await api.get(`/agendamentos/cliente/${form.value.id}`)
    agendamentos.value = Array.isArray(data) ? data : []
  } catch {
    agendamentos.value = []
  } finally {
    agendamentosLoading.value = false
  }
}

const workspaceIdRef = toRef(props, 'workspaceId')
const {
  canDeleteAgendamento,
  confirmDeleteTarget,
  deleting,
  loadStaffRole,
  requestDeleteAgendamento,
  cancelDeleteAgendamento,
  confirmDeleteAgendamento
} = useAgendamentoStaffDelete(workspaceIdRef, loadAgendamentos)

const deleteConfirmMessage = computed(() => {
  const ag = confirmDeleteTarget.value
  if (!ag) return t('workspaceClienteModal.agendamento.deleteConfirmMessage')
  const date = ag.dataAgendamento || ''
  const time = ag.horario || ''
  return t('workspaceClienteModal.agendamento.deleteConfirmMessageDetail', { date, time })
})

const clienteIdRef = computed(() => form.value.id || null)

const onRecebivelDeletedFromApi = (list) => {
  if (Array.isArray(list)) recebiveis.value = list
}

const {
  canDeleteRecebivel,
  confirmDeleteTarget: confirmDeleteRecebivelTarget,
  deleting: deletingRecebivel,
  requestDeleteRecebivel,
  cancelDeleteRecebivel,
  confirmDeleteRecebivel
} = useRecebivelStaffDelete({
  workspaceIdRef,
  clienteIdRef,
  onDeleted: onRecebivelDeletedFromApi
})

const deleteRecebivelConfirmMessage = computed(() => {
  const item = confirmDeleteRecebivelTarget.value
  if (!item) return t('workspaceClienteModal.financeiro.deleteConfirmMessage')
  const valor = formatMoneyAmount(item.valor || 0, locale.value)
  const date = formatDateOnly(item.dataVencimento)
  return t('workspaceClienteModal.financeiro.deleteConfirmMessageDetail', { valor, date })
})

const onScheduled = async () => {
  showScheduleModal.value = false
  await loadAgendamentos()
}

const updateAgendamentoStatus = async ({ id, status }) => {
  try {
    await api.patch(`/agendamentos/cliente/agendamento/${id}/status`, { status })
    await loadAgendamentos()
  } catch (error) {
    console.error('Erro ao atualizar status do agendamento:', error)
  }
}

const openRecebivelModal = (item = null) => {
  if (item) {
    recebivelEditingId.value = item.id
    recebivelEdit.value = {
      valor: item.valor,
      dataVencimento: item.dataVencimento || '',
      descricao: item.descricao || ''
    }
  } else {
    recebivelEditingId.value = null
    recebivelEdit.value = null
  }
  showRecebivelForm.value = true
}

const closeRecebivelModal = () => {
  showRecebivelForm.value = false
  recebivelEditingId.value = null
  recebivelEdit.value = null
}

let unregisterRecebivelEscape = null

watch(showRecebivelForm, (open) => {
  if (unregisterRecebivelEscape) {
    unregisterRecebivelEscape()
    unregisterRecebivelEscape = null
  }
  if (open) {
    unregisterRecebivelEscape = pushEscapeLayer(closeRecebivelModal)
  }
})

const onRecebivelSaved = async (payload) => {
  const { valor, dataVencimento, descricao, parcelas } = payload
  if (recebivelEditingId.value) {
    const idx = recebiveis.value.findIndex(r => r.id === recebivelEditingId.value)
    if (idx >= 0) {
      recebiveis.value[idx] = {
        ...recebiveis.value[idx],
        valor,
        dataVencimento,
        descricao: descricao || ''
      }
    }
  } else if (Array.isArray(parcelas) && parcelas.length) {
    recebiveis.value.push(...parcelas)
  } else {
    recebiveis.value.push({
      id: `r_${Date.now()}`,
      valor,
      dataVencimento,
      descricao: descricao || '',
      pago: false,
      dataPagamento: null
    })
  }
  closeRecebivelModal()
  await saveRecebiveis()
}

const toggleRecebivelPago = async (item) => {
  const idx = recebiveis.value.findIndex(r => r.id === item.id)
  if (idx < 0) return
  const nextPago = !recebiveis.value[idx].pago
  const hoje = new Date().toISOString().slice(0, 10)
  recebiveis.value[idx] = {
    ...recebiveis.value[idx],
    pago: nextPago,
    dataPagamento: nextPago ? hoje : null
  }
  await saveRecebiveis()
}

const resetNestedModals = () => {
  sideDrawerTab.value = null
  showUserSelector.value = false
  showScheduleModal.value = false
  enderecoModalOpen.value = false
  enderecoEdit.value = null
  enderecoEditIndex.value = -1
  contatoModalOpen.value = false
  contatoEdit.value = null
  contatoEditIndex.value = -1
  showRecebivelForm.value = false
  recebivelEditingId.value = null
  recebivelEdit.value = null
}

const arquivarCliente = async () => {
  if (!form.value.id || archivingCliente.value) return

  const result = await Swal.fire({
    title: t('workspaceClienteModal.archiveTitle'),
    html: `
      <p style="margin-bottom: 12px;">${t('workspaceClienteModal.archiveConfirm')}</p>
      <p style="color: #64748b; font-size: 14px;">${t('workspaceClienteModal.archiveHint')}</p>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('workspaceClienteModal.confirmArchive'),
    cancelButtonText: t('common.cancel'),
    reverseButtons: true
  })

  if (!result.isConfirmed) return

  archivingCliente.value = true
  try {
    await api.put(`/clientes/${form.value.id}/arquivar`)
    toast.success(t('workspaceClienteModal.archivedText'))
    resetNestedModals()
    emit('archived', form.value.id)
    emit('close')
  } catch (error) {
    console.error('Erro ao arquivar cliente:', error)
    await Swal.fire({
      title: t('workspaceClienteModal.archiveErrorTitle'),
      text: t('workspaceClienteModal.archiveErrorText'),
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#ef4444'
    })
  } finally {
    archivingCliente.value = false
  }
}

const close = () => {
  resetNestedModals()
  emit('close')
}

const handleEscapeKey = (event) => {
  if (event.key !== 'Escape' || !panelActive.value) return

  if (sideDrawerTab.value) {
    closeSideDrawer()
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }

  if (enderecoModalOpen.value) {
    enderecoModalOpen.value = false
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }
  if (contatoModalOpen.value) {
    contatoModalOpen.value = false
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }
  if (showRecebivelForm.value) {
    closeRecebivelModal()
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }
  if (showUserSelector.value) {
    showUserSelector.value = false
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }
  if (showScheduleModal.value) {
    showScheduleModal.value = false
    event.preventDefault()
    event.stopImmediatePropagation()
    return
  }

  event.preventDefault()
  event.stopImmediatePropagation()
  close()
}

const syncEscapeListener = (open) => {
  window.removeEventListener('keydown', handleEscapeKey, true)
  if (open) {
    window.addEventListener('keydown', handleEscapeKey, true)
  }
}

watch(panelActive, (open) => {
  syncEscapeListener(open)
  if (!open) resetNestedModals()
}, { immediate: true })

onBeforeUnmount(() => {
  if (unregisterRecebivelEscape) {
    unregisterRecebivelEscape()
    unregisterRecebivelEscape = null
  }
  window.removeEventListener('keydown', handleEscapeKey, true)
})

const loadPlanFeatures = async () => {
  if (!props.workspaceId) return
  await planoFeatures.loadForWorkspace(props.workspaceId, true)
  planLoaded.value = true
}

const resetClienteForm = () => {
  form.value = emptyForm()
  recebiveis.value = []
  agendamentos.value = []
}

const loadClienteFromApi = async (clienteId, { showLoading = true } = {}) => {
  if (showLoading) clienteLoading.value = true
  try {
    const { data } = await api.get(`/clientes/${clienteId}`)
    applyCliente(data)
    await loadAgendamentos()
  } catch (error) {
    console.error('Erro ao carregar cliente:', error)
    toast.error(t('workspaceClienteModal.validation.loadError'), { position: POSITION.BOTTOM_RIGHT })
    emit('close')
  } finally {
    if (showLoading) clienteLoading.value = false
  }
}

const emitTitleChange = () => {
  if (props.asPage && form.value.nome) {
    emit('title-change', form.value.nome)
  }
}

watch(
  () => (panelActive.value ? resolvedClienteId.value : null),
  async (idNum, prevId) => {
    if (!panelActive.value) {
      if (prevId != null) {
        resetClienteForm()
        resetNestedModals()
      }
      loadedClienteId.value = null
      clienteLoading.value = false
      return
    }
    activeTab.value = 'dados'
    await loadPlanFeatures()
    await refreshWhatsAppStatus()
    if (idNum) {
      if (loadedClienteId.value === idNum && form.value.id === idNum) {
        return
      }
      await loadClienteFromApi(idNum)
      loadedClienteId.value = idNum
      return
    }
    loadedClienteId.value = null
    clienteLoading.value = false
    if (props.cliente) {
      applyCliente({ ...emptyForm(), ...props.cliente, workspaceId: props.workspaceId })
    } else {
      resetClienteForm()
    }
    agendamentos.value = []
  },
  { immediate: true }
)

watch(() => props.workspaceId, () => {
  if (panelActive.value) loadPlanFeatures()
})

watch(() => form.value.nome, () => emitTitleChange())

watch(activeTab, async (tab) => {
  if (!props.asPage && tab === 'whatsapp' && showWhatsAppTab.value) {
    await refreshWhatsAppStatus()
    await nextTick()
    await waPanelRef.value?.refreshWhatsAppStatus?.()
    await waPanelRef.value?.loadChatHistory?.()
  }
})

if (typeof window !== 'undefined') {
  window.addEventListener('whatsapp.connected', () => {
    if (panelActive.value) refreshWhatsAppStatus()
  })
  window.addEventListener('whatsapp.disconnected', () => {
    if (panelActive.value) refreshWhatsAppStatus()
  })
}
</script>

<style scoped>
.cliente-detail-page-root {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.cliente-page-panel {
  min-height: 0;
}
.cliente-page-panel--fill {
  flex: 1 1 auto;
  min-height: min(70vh, 720px);
  height: 100%;
  max-height: calc(100vh - 12rem);
}
.cliente-side-drawer-pane--fill {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}
:deep(.cliente-side-drawer-body--flush .whatsapp-chat-panel) {
  flex: 1;
  min-height: 0;
  height: 100%;
}
:deep(.cliente-side-drawer-body--flush .cliente-side-drawer-pane) {
  flex: 1;
  min-height: 0;
}

.cliente-side-drawer-pane--financeiro {
  --fin-drawer-radius: 4px;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.cliente-financeiro-drawer {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.cliente-financeiro-drawer__toolbar {
  flex-shrink: 0;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-primary, #e2e8f0);
}

.cliente-financeiro-drawer__hint {
  margin: 0 0 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.45;
}

.cliente-financeiro-drawer__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 100%;
  padding: 0.625rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: var(--fin-drawer-radius, 4px);
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.12s ease;
}

.cliente-financeiro-drawer__add svg {
  width: 1rem;
  height: 1rem;
}

.cliente-financeiro-drawer__add:hover {
  filter: brightness(1.06);
}

.cliente-financeiro-drawer__add:active {
  transform: scale(0.99);
}

.cliente-financeiro-drawer__stats {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.625rem;
  padding: 0.875rem 1rem;
}

.cliente-financeiro-drawer__stat {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem;
  border-radius: var(--fin-drawer-radius, 4px);
  border: 1px solid var(--border-primary, #e2e8f0);
  min-width: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: opacity 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.cliente-financeiro-drawer__stat--filter-active {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--info) 35%, transparent);
}

.cliente-financeiro-drawer__stat--filter-inactive {
  opacity: 0.48;
}

.cliente-financeiro-drawer__stat--filter-inactive:hover {
  opacity: 0.72;
}

.cliente-financeiro-drawer__stat:focus-visible {
  outline: 2px solid var(--info);
  outline-offset: 2px;
}

.cliente-financeiro-drawer__stat-icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--fin-drawer-radius, 4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cliente-financeiro-drawer__stat-icon svg {
  width: 1.125rem;
  height: 1.125rem;
}

.cliente-financeiro-drawer__stat-body {
  flex: 1;
  min-width: 0;
}

.cliente-financeiro-drawer__stat--alert {
  grid-column: 1 / -1;
}

.cliente-financeiro-drawer__stat-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cliente-financeiro-drawer__stat-value {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
  word-break: break-word;
}

.cliente-financeiro-drawer__stat-meta {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.6875rem;
  line-height: 1.3;
}

.cliente-financeiro-drawer__empty {
  margin: 1rem;
}

.cliente-financeiro-drawer__list-wrap {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cliente-financeiro-drawer__list-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.625rem 1rem 0.5rem;
}

.cliente-financeiro-drawer__list-title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.cliente-financeiro-drawer__list-count {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 1.4;
}

.cliente-financeiro-drawer__filter-empty {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  text-align: center;
}

.cliente-financeiro-drawer__filter-empty p {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
}

.cliente-financeiro-drawer__filter-reset {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: var(--fin-drawer-radius, 4px);
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
}

.cliente-financeiro-drawer__filter-reset:hover {
  background: var(--bg-hover);
}

.cliente-financeiro-drawer__list {
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.625rem;
  scrollbar-gutter: stable;
}

.cliente-financeiro-drawer__list > :deep(.recebivel-card--drawer) {
  flex: 0 0 auto;
  flex-shrink: 0;
  height: auto;
  min-height: unset;
  overflow: visible;
}

.cliente-financeiro-drawer__list::-webkit-scrollbar {
  width: 6px;
}

.cliente-financeiro-drawer__list::-webkit-scrollbar-thumb {
  border-radius: var(--fin-drawer-radius, 4px);
  background: color-mix(in srgb, var(--text-muted) 55%, transparent);
}

.cliente-financeiro-drawer__list::-webkit-scrollbar-track {
  background: transparent;
}

.cliente-modal-panel {
  --cliente-radius: 4px;
  border-radius: var(--cliente-radius);
}
.cliente-modal-panel :deep(
  .cliente-detail-section,
  .cliente-detail-section__icon,
  .cliente-list-item,
  .cliente-list-item__icon,
  .cliente-list-item__badge,
  .cliente-form-field__control,
  .cliente-icon-btn,
  .cliente-empty-state,
  .cliente-modal-hint,
  .cliente-modal-btn,
  .cliente-modal-tab-bar__item,
  .workspace-tag-chip,
  .workspace-tags-picker__count,
  .cliente-assigned-users__row,
  .cliente-assigned-users__avatar,
  .cliente-assigned-users__remove,
  .cliente-assigned-users__add,
  .ag-card-pro,
  .ag-card-pro__date-box,
  .ag-card-pro__ribbon,
  .recebivel-card,
  .recebivel-card__icon,
  .recebivel-card__badge,
  .recebivel-card__btn,
  .cliente-list-card,
  .cliente-list-btn
) {
  border-radius: var(--cliente-radius) !important;
}
.cliente-modal-panel :deep([class*='rounded-']) {
  border-radius: var(--cliente-radius) !important;
}
.cliente-modal-loading__spinner {
  border-radius: 50% !important;
}
.cliente-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  border-radius: 4px;
  border: 2px dashed var(--border-primary);
}
.cliente-empty-state__icon {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 0.75rem;
  color: var(--text-muted);
}
.cliente-modal-body {
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1.25rem;
}
.cliente-modal-body--flush {
  padding: 0;
}

:deep(.cliente-modal-tab-bar__archive-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: var(--text-tertiary, #94a3b8);
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

:deep(.cliente-modal-tab-bar__archive-btn svg) {
  width: 17px;
  height: 17px;
}

:deep(.cliente-modal-tab-bar__archive-btn:hover:not(:disabled)) {
  color: #dc2626;
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.22);
}

:deep(.cliente-modal-tab-bar__archive-btn:disabled) {
  opacity: 0.45;
  cursor: not-allowed;
}
.cliente-modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  gap: 0.75rem;
  color: var(--text-secondary);
}
.cliente-modal-loading__spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid var(--border-primary);
  border-top-color: var(--info);
  border-radius: 50%;
  animation: cliente-modal-spin 0.7s linear infinite;
}
@keyframes cliente-modal-spin {
  to { transform: rotate(360deg); }
}
.cliente-tab-pane {
  min-height: min-content;
}
.cliente-tab-pane--fill {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.status-fade-enter-active,
.status-fade-leave-active {
  transition: opacity 0.2s ease;
}
.status-fade-enter-from,
.status-fade-leave-to {
  opacity: 0;
}
.cliente-modal-hint {
  font-size: 0.8125rem;
  margin: 0 0 1rem;
  padding: 0.75rem 1rem;
  border-radius: 4px;
}
.cliente-modal-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
}
.cliente-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.75rem;
  padding: 0.625rem 1.25rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  cursor: pointer;
}
.cliente-modal-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.cliente-modal-btn--primary {
  border: none;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35), 0 1px 2px rgba(15, 23, 42, 0.08);
}
.cliente-modal-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 50%, #0284c7 100%);
  box-shadow: 0 6px 20px rgba(14, 165, 233, 0.45), 0 2px 4px rgba(15, 23, 42, 0.1);
}
.cliente-modal-btn--primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.cliente-modal-btn__icon {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
}
.cliente-modal-btn__icon--spin {
  animation: cliente-modal-spin 0.7s linear infinite;
}
@media (max-width: 480px) {
  .cliente-modal-footer {
    flex-direction: column-reverse;
  }
  .cliente-modal-btn {
    width: 100%;
  }
}
.cliente-dados-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}
@media (min-width: 1024px) {
  .cliente-dados-layout {
    grid-template-columns: 1.15fr 0.85fr;
  }
}
.cliente-list-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid var(--border-primary, #e2e8f0);
  background: var(--bg-primary, #f8fafc);
  margin-bottom: 0.5rem;
}
.cliente-list-btn {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  color: #0ea5e9;
}
.cliente-list-btn--danger {
  color: #ef4444;
}
</style>

<!-- Histórico no drawer: texto escuro no tema claro (somente este textarea) -->
<style>
body.tema_clara .cliente-side-drawer-backdrop textarea[data-chat-input],
html.light .cliente-side-drawer-backdrop textarea[data-chat-input] {
  color: #161616 !important;
  -webkit-text-fill-color: #161616 !important;
}
</style>
